import cls from './rule-drawer.module.scss';
import { linkifyText } from 'shared/helpers/linkifyText';
import { Icon } from 'shared/ui/icon/icon';
import { ICONS } from 'shared/constants/images';
import { translate } from 'services/translationService';

/**
 * Findings keys come straight from the API as snake_case, so they are localized
 * via the `finding_<key>` convention. Keys the backend adds later fall back to
 * the humanized English form instead of rendering a raw slug.
 */
const FINDING_KEY_PREFIX = 'finding_';

const formatTitle = (value: string): string => {
  if (!value) return '';
  return value
    .replace(/_/g, ' ') // underscores to spaces
    .split(/\s+/)
    .map(word =>
      word.length === 0
        ? ''
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    )
    .join(' ')
    .trim();
};

const translateTitle = (key: string): string =>
  translate(`${FINDING_KEY_PREFIX}${key}`, '_global', formatTitle(key));

const renderBooleanIcon = (value: boolean): React.ReactNode => (
  <Icon pathToIcon={value ? ICONS.checkMarkGreen : ICONS.cross} size={20} />
);

const renderValue = (value: any): React.ReactNode => {
  if (value === null || value === undefined) return <p>-</p>;
  if (typeof value === 'boolean') {
    return renderBooleanIcon(value);
  }
  if (typeof value === 'string' || typeof value === 'number') {
    return (
      <span>
        {typeof value === 'string' ? linkifyText(value) : String(value)}
      </span>
    );
  }
  if (Array.isArray(value)) {
    if (value.length === 0)
      return <p>{translate('findingsNone', '_global', 'None')}</p>;
    return (
      <ul className={cls.findingsList}>
        {value.map((el, idx) => (
          <li key={idx} className={cls.findingsListItem}>
            {typeof el === 'object' && el !== null
              ? renderNestedObject(el)
              : typeof el === 'string'
                ? linkifyText(el)
                : String(el)}
          </li>
        ))}
      </ul>
    );
  }
  // object
  return renderNestedObject(value);
};

const renderNestedObject = (obj: Record<string, any>) => (
  <div className={cls.findingsObject}>
    {Object.entries(obj).map(([k, v]) => (
      <div key={k} className={cls.findingsRow}>
        <p className={cls.findingsKey}>{translateTitle(k)}:</p>
        <div className={cls.findingsValue}>{renderValue(v)}</div>
      </div>
    ))}
  </div>
);

export const renderFindingsRoot = (findings: any) => {
  if (
    !findings ||
    (typeof findings === 'object' && Object.keys(findings).length === 0)
  ) {
    return (
      <p className={cls.textGray}>
        {translate('findingsNotFound', '_global', 'Not Found')}
      </p>
    );
  }
  if (typeof findings !== 'object' || Array.isArray(findings)) {
    return renderValue(findings);
  }
  return Object.entries(findings).map(([key, value]) =>
    typeof value === 'boolean' ? (
      <div key={key} className={cls.findingsBoolRow}>
        <span>{translateTitle(key)}</span>
        {renderBooleanIcon(value)}
      </div>
    ) : typeof value === 'string' || typeof value === 'number' ? (
      <div key={key} className={cls.findingsSection}>
        <p className={cls.findingsTitle}>
          {translateTitle(key)}:{' '}
          <span>
            {typeof value === 'string' ? linkifyText(value) : String(value)}
          </span>
        </p>
      </div>
    ) : (
      <div key={key} className={cls.findingsSection}>
        <h5 className={cls.findingsTitle}>{translateTitle(key)}</h5>
        <div className={cls.findingsContent}>{renderValue(value)}</div>
      </div>
    )
  );
};
